// Admin-approved prorated late enrollment (Class Editor / Roster).
//
// The admin approves a price for one family + class + session. The family then
// registers through the normal public form and the SERVER recognises their
// email and charges the approved amount. Nothing here sets a checkout price.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BLOCKER_MESSAGES,
  authorizationStatusLabel,
  cancelProratedAuthorization,
  createProratedAuthorization,
  listProratedAuthorizations,
  money,
  parseDollarsToCents,
  previewProration,
  validateOverride,
  type ProratedAuthorization,
  type ProrationCalculation,
} from "@/lib/proratedEnrollment";
import type { ClassSession } from "@/data/classes";

interface Props {
  slug: string;
  sessions: ClassSession[];
  isAdmin: boolean;
}

const fmtDate = (iso: string) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

export default function ProratedEnrollmentPanel({ slug, sessions, isAdmin }: Props) {
  const [rows, setRows] = useState<ProratedAuthorization[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const sessionLabels = useMemo(
    () => (sessions ?? []).map((s: any) => String(s?.label ?? "")).filter(Boolean),
    [sessions],
  );

  const [sessionLabel, setSessionLabel] = useState<string>("");
  const [email, setEmail] = useState("");
  const [parentName, setParentName] = useState("");
  const [maxChildren, setMaxChildren] = useState("1");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [calc, setCalc] = useState<ProrationCalculation | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      setRows(await listProratedAuthorizations(slug));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [slug, isAdmin]);

  useEffect(() => { void load(); }, [load]);

  const resetForm = () => {
    setSessionLabel(sessionLabels[sessionLabels.length - 1] ?? "");
    setEmail(""); setParentName(""); setMaxChildren("1");
    setAmount(""); setReason(""); setNotes(""); setCalc(null);
  };

  const runPreview = async (label: string) => {
    setPreviewing(true);
    setCalc(null);
    try {
      const c = await previewProration(slug, label);
      setCalc(c);
      setAmount((c.calculated_price_cents / 100).toFixed(2));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setPreviewing(false);
    }
  };

  const approvedCents = parseDollarsToCents(amount);
  const check = calc
    ? validateOverride(approvedCents, calc.calculated_price_cents, calc.normal_price_cents, reason)
    : { ok: false, error: null };
  const isOverride = Boolean(calc && approvedCents !== null && approvedCents !== calc.calculated_price_cents);

  const submit = async () => {
    if (!calc?.ok || !check.ok || approvedCents === null) return;
    setSaving(true);
    try {
      await createProratedAuthorization({
        classId: slug,
        sessionLabel: calc.session_label,
        parentEmail: email,
        parentName: parentName || null,
        approvedPriceCents: approvedCents,
        overrideReason: reason,
        maxChildren: Number(maxChildren) || 1,
        adminNotes: notes || null,
      });
      toast.success(`Approved ${money(approvedCents)} for ${email}. They can register normally now.`);
      setOpen(false);
      resetForm();
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (id: string) => {
    try {
      await cancelProratedAuthorization(id);
      toast.success("Approval cancelled.");
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="rounded-md border bg-background/60 p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-xs font-medium">Prorated late enrollment</div>
          <p className="text-[10px] text-muted-foreground">
            Approve a reduced price for a family joining after the class started. They register on the
            normal page and are charged the approved amount automatically.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => { resetForm(); setOpen(true); }}>
          Create prorated enrollment
        </Button>
      </div>

      <div className="mt-3 space-y-2">
        {loading && <p className="text-xs text-muted-foreground">Loading…</p>}
        {!loading && rows.length === 0 && (
          <p className="text-xs text-muted-foreground">No prorated approvals for this class.</p>
        )}
        {rows.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded border px-2 py-1.5 text-xs">
            <div className="min-w-0">
              <div className="truncate font-medium">{r.parent_name || r.parent_email}</div>
              <div className="text-[10px] text-muted-foreground">
                {r.session_label} · normal {money(r.normal_price_cents)} · calculated{" "}
                {money(r.calculated_price_cents)} · approved <strong>{money(r.approved_price_cents)}</strong>
                {r.override_reason ? ` · override: ${r.override_reason}` : ""}
              </div>
              <div className="text-[10px] text-muted-foreground">
                {r.remaining_meetings} of {r.total_meetings} meetings remaining · approved {fmtDate(r.approved_at)} ·
                {r.status === "used" ? ` paid ${fmtDate(r.consumed_at ?? "")}` : ` expires ${fmtDate(r.expires_at)}`}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={r.status === "used" ? "secondary" : "outline"}>{authorizationStatusLabel(r)}</Badge>
              {(r.status === "active" || r.status === "reserved") && (
                <Button size="sm" variant="ghost" onClick={() => cancel(r.id)}>Cancel</Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create prorated enrollment</DialogTitle>
            <DialogDescription>
              Pick the session, check the math, then approve a price for one family.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Session</Label>
              <Select
                value={sessionLabel}
                onValueChange={(v) => { setSessionLabel(v); void runPreview(v); }}
              >
                <SelectTrigger><SelectValue placeholder="Choose a session" /></SelectTrigger>
                <SelectContent>
                  {sessionLabels.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {previewing && <p className="text-xs text-muted-foreground">Checking the schedule…</p>}

            {calc && !calc.ok && (
              <div className="rounded border border-destructive/40 bg-destructive/5 p-2 text-xs">
                {calc.blockers.map((b) => (
                  <div key={b}>{BLOCKER_MESSAGES[b] ?? b}</div>
                ))}
              </div>
            )}

            {calc?.ok && (
              <div className="rounded border bg-muted/40 p-2 text-xs">
                <div>Normal price: <strong>{money(calc.normal_price_cents)}</strong></div>
                <div>
                  Meetings: {calc.total_meetings} total · {calc.cancelled_meetings} cancelled ·{" "}
                  {calc.completed_meetings} already held · <strong>{calc.remaining_meetings} remaining</strong>
                </div>
                <div>
                  {money(calc.normal_price_cents)} ÷ {calc.chargeable_meetings} meetings ={" "}
                  {money(calc.per_meeting_cents)} per meeting × {calc.remaining_meetings} remaining ={" "}
                  <strong>{money(calc.calculated_price_cents)}</strong>
                </div>
                {calc.warnings.map((w) => (
                  <div key={w} className="mt-1 text-[10px] text-amber-700 dark:text-amber-400">{w}</div>
                ))}
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Parent email (must match at checkout)</Label>
                <Input value={email} onChange={(e) => setEmail(e.target.value.trim())} placeholder="parent@example.com" />
              </div>
              <div>
                <Label className="text-xs">Parent name (optional)</Label>
                <Input value={parentName} onChange={(e) => setParentName(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Children covered</Label>
                <Input value={maxChildren} onChange={(e) => setMaxChildren(e.target.value.replace(/\D/g, "") || "1")} />
                <p className="mt-1 text-[10px] text-muted-foreground">
                  Each covered child pays the approved amount. Extra children pay the normal price.
                </p>
              </div>
              <div>
                <Label className="text-xs">Approved price per child</Label>
                <Input value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
            </div>

            {isOverride && (
              <div>
                <Label className="text-xs">Why is this different from the calculated amount?</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Internal note" />
              </div>
            )}

            <div>
              <Label className="text-xs">Internal notes (optional)</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>

            {check.error && <p className="text-xs text-destructive">{check.error}</p>}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={!calc?.ok || !check.ok || saving || !email.includes("@")}
            >
              {saving ? "Approving…" : "Approve price"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
